// frontend/src/theme.js — appearance source of truth. Values: 'light' | 'dark'.
import { useSyncExternalStore } from 'react';

const KEY = 'mp_theme';
const listeners = new Set();
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// First-visit default = dark (brand). To follow the OS on first load instead,
// swap FIRST_VISIT for the matchMedia line below.
const FIRST_VISIT = 'dark';
// const FIRST_VISIT = window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';

export function getTheme(){
  return document.documentElement.getAttribute('data-theme')
      || localStorage.getItem(KEY) || FIRST_VISIT;
}

function syncMeta(t){
  let m = document.querySelector('meta[name="theme-color"]');
  if(!m){ m = document.createElement('meta'); m.name = 'theme-color'; document.head.appendChild(m); }
  m.setAttribute('content', t === 'light' ? '#F5F3EF' : '#000000');
}

export function initTheme(){
  const t = getTheme();
  document.documentElement.setAttribute('data-theme', t);
  syncMeta(t);
  // enable toggle-time transitions only AFTER first paint (no flash on load)
  requestAnimationFrame(() =>
    requestAnimationFrame(() => document.documentElement.classList.add('theme-ready')));
}

function commit(next){
  document.documentElement.setAttribute('data-theme', next);
  try { localStorage.setItem(KEY, next); } catch (e) {}
  syncMeta(next);
  listeners.forEach(fn => fn());
}

function ripple(x, y, far){
  if (reduceMotion()) return;
  const r = document.createElement('div');
  r.className = 'theme-ripple';
  r.style.cssText = `left:${x}px;top:${y}px;width:${far*2}px;height:${far*2}px`;
  document.body.appendChild(r);
  setTimeout(() => r.remove(), 640);
}

export function setTheme(next, origin){
  if (next === getTheme()) return;
  const canReveal = typeof document.startViewTransition === 'function' && !reduceMotion() && origin;
  if (!canReveal){ commit(next); return; }
  const rect = origin.getBoundingClientRect();
  const x = rect.left + rect.width / 2, y = rect.top + rect.height / 2;
  const far = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));
  ripple(x, y, far);
  const vt = document.startViewTransition(() => commit(next));
  vt.ready.then(() => {
    document.documentElement.animate(
      { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${far}px at ${x}px ${y}px)`] },
      { duration: 560, easing: 'cubic-bezier(.42,0,.18,1)', pseudoElement: '::view-transition-new(root)' }
    );
  });
}

export function toggleTheme(origin){
  setTheme(getTheme() === 'dark' ? 'light' : 'dark', origin);
}

// React hook — re-renders subscribers (for aria-labels) when the theme changes.
export function useTheme(){
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    getTheme,
    () => 'dark'
  );
}
